/**
 * PLAY WARM-UP — behind the boot loader, draw every picture play will show and, on Pixi,
 * upload it to the GPU, so no Fall or Merge frame draws or uploads art.
 *
 * Runs after the menu asset preload (assets/asset-preloader.js: sounds, the gameplay pictures,
 * the tier composites) and before the first Fall starts (Game#warmMenuAssetsIfNeeded, which
 * startFromMenu waits for), one job per task so the loader keeps animating, most important
 * first:
 *   1. juice FX atlas: base page, the Pixi drawer and its textures, then the large page;
 *   2. sky: sun (day + sunset, disc + rays) and moon at the radius and density the sky uses,
 *      cloud / halo disc, star sprites, shooting-star streak;
 *   3. creature layers (disc, creature, blink, glint) of every active tier, at the bake scale
 *      the larger of the Fall pickup and the Merge jar needs;
 *   4. Fall objects (boulder, gem, shield, flask, cracks, shards, buoy) and Fall FX pictures;
 *   5. lettering, once the fonts are in: 3-2-1, every callout, every catch / merge score;
 *   6. glow sprite, jar marks (rings, badges, ghost, aim) and the Merge jar glass;
 *   7. pop-ups: the large creature and puzzle-piece pictures (PNG-encoded in the background)
 *      and the wheel face.
 * Sizes come from the same functions the draw sites use, and the Merge jar's from the layout
 * the Merge stage will get (Game#_getLayoutPreview) through the radius math the physics uses
 * (flask/body-sizing.js). Render-only: nothing here touches gameplay state, physics, analytics
 * or portal calls. The idle-time prewarms in core/game.js stay as a safety net.
 */
import * as C from "../config/constants.js";
import { PUZZLE_PIECE_LAYOUT } from "../config/puzzle-reward.js";
import { MergeAssets } from "../assets/merge-assets.js";
import { GAMEPLAY_STATIC_IMAGE_KEYS, gameImageUrl } from "../assets/game-image-urls.js";
import { getCachedImage } from "../assets/image-cache.js";
import { computeFlaskBodySizing } from "../flask/body-sizing.js";
import { buildFlaskGeometry } from "../flask/system/geometry.js";
import { buildFallRunConfig } from "../run/fall-run-config.js";
import { FallRenderer } from "../run/fall-renderer.js";
import { bakeScorePopupText, computeScorePopupPeakFontPx } from "../run/score-popups.js";
import { skyBodyRadii } from "../render/sky-scene.js";
import { getShakeJarArt, layoutShakeJarChrome } from "../render/shake-jar-visual.js";
import { TIER_ART_SIZES, imageArtKey } from "../render/toon/art-catalog.js";
import { getToonArtCanvas, warmToonArtUrl } from "../render/toon/art-registry.js";
import { FALL_FX_ART, getFallFxArt } from "../render/toon/fall/fall-fx-art.js";
import { displayBoost } from "../render/toon/jar/jar-bodies.js";
import { jarMarkArtKeys } from "../render/toon/jar/jar-marks.js";
import {
  getCelestialArt,
  getLobeDiscCanvas,
  getStarCanvas,
  getStreakCanvas,
} from "../render/toon/sky-art.js";

/** Creature layers in the order a body shows them (render/toon/jar/jar-bodies.js). */
var CREATURE_LAYERS = ["disc", "body", "shine", "blink"];
/**
 * Safety cap: past this (wall ms), what is left runs in idle time after the loader instead of
 * holding it. Real GPUs finish in well under a second; a CPU-emulated GPU (SwiftShader) in
 * about 2.5–4 s.
 */
var WARM_BUDGET_MS = 6000;
/** The fonts the lettering is baked in get at most this long (they are local files). */
var FONT_WAIT_MS = 2500;
/**
 * Background PNG encodes (pop-up pictures) hold the loader at most this long; one still running
 * then finishes on its own (its URL is memoised when it lands).
 */
var ENCODE_WAIT_MS = 1000;
/** Fenced steps allowed on the GPU at once (the next step is issued while one still runs). */
var GPU_IN_FLIGHT = 1;
/** A fence is waited for at most this long before the next step goes anyway. */
var GPU_WAIT_MS = 500;
var FONT_PROBES = ["700 24px Fredoka", "700 48px Fredoka", "700 24px 'Baloo 2'"];

function now() {
  return typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();
}

function delay(ms) {
  return new Promise(function (resolve) { setTimeout(resolve, ms); });
}

/**
 * Yield to the event loop (input, the loader's animation frames) between jobs. A message
 * channel rather than setTimeout(0): nested timeouts are clamped to 4 ms each after a few
 * levels, which over a hundred-odd jobs would only add idle wait to the loader.
 */
var channel = null;
var channelQueue = [];
function nextTask() {
  if (typeof MessageChannel === "function") {
    if (!channel) {
      channel = new MessageChannel();
      channel.port1.onmessage = function () {
        var resolve = channelQueue.shift();
        if (resolve) resolve();
      };
    }
    return new Promise(function (resolve) {
      channelQueue.push(resolve);
      channel.port2.postMessage(0);
    });
  }
  return delay(0);
}

function canDraw2d() {
  if (typeof document === "undefined" || typeof document.createElement !== "function") return false;
  if (typeof navigator !== "undefined" && /jsdom/i.test(navigator.userAgent || "")) return false;
  try {
    var probe = document.createElement("canvas");
    return !!(probe && probe.getContext && probe.getContext("2d"));
  } catch (_e) {
    return false;
  }
}

function loadFonts() {
  if (typeof document === "undefined" || !document.fonts || typeof document.fonts.load !== "function") {
    return Promise.resolve();
  }
  var loads = FONT_PROBES.map(function (probe) {
    try {
      return Promise.resolve(document.fonts.load(probe)).catch(function () {});
    } catch (_e) {
      return Promise.resolve();
    }
  });
  return Promise.race([Promise.all(loads), delay(FONT_WAIT_MS)]);
}

function activeChain() {
  return typeof C.getActiveEvolutionChain === "function"
    ? C.getActiveEvolutionChain()
    : (C.EVOLUTION_CHAIN || []);
}

function fallRunConfig(game) {
  try {
    return buildFallRunConfig({ cycleIndex: game.currentCycleIndex || 0, playerHp: game.savedFlaskHp });
  } catch (_e) {
    return C.RUN_CONFIG || {};
  }
}

/** Device px per CSS px of the playfield (Pixi resolution or the 2D canvas DPR). */
function deviceScale(game) {
  if (typeof game._getPlayfieldDeviceScale === "function") {
    var s = game._getPlayfieldDeviceScale();
    if (s > 0) return s;
  }
  return MergeAssets.canvasDeviceScale();
}

/**
 * Report how big each tier is shown in the Fall and in the Merge jar, the way the draw sites
 * report it (assets/merge-assets.js noteLayerSize), before anything is baked: the bake scale of
 * a layer (and of the shared glint) is then final, so nothing is re-baked finer mid-play.
 */
function noteCreatureSizes(game, chain, fallLayout, mergeSizing, res) {
  var cfg = fallRunConfig(game);
  // Fall: every pickup has the run-width share createMergePickups gives it (run/layout.js); the
  // renderer reports the long side of getFallObjectDrawSize (height = width / image aspect).
  var widthFrac = cfg.MERGE_PICKUP_WIDTH_RATIO || 0.028;
  var pixelW = fallLayout && fallLayout.runWidth > 0
    ? C.fallObjectPixelWidth(widthFrac, fallLayout.runWidth, cfg)
    : 0;
  var radii = mergeSizing ? mergeSizing.bodyRadiusByTier : null;
  var defaultAspect = C.MERGE_BODY_ELLIPSE_ASPECT_DEFAULT != null ? C.MERGE_BODY_ELLIPSE_ASPECT_DEFAULT : 1;
  for (var t = 0; t < chain.length; t++) {
    if (pixelW > 0) {
      var frame = MergeAssets.frame(t);
      var aspect = frame && frame.h > 0 ? frame.w / frame.h : 1;
      MergeAssets.noteLayerSize(t, Math.max(pixelW, pixelW / aspect) * res, "fall");
    }
    var r = radii && radii[t];
    if (!(r > 0)) continue;
    // Jar: the body's radii (flask/system/bodies.js) times the drawn boost
    // (render/toon/jar/jar-bodies.js), laid out like the body drawer lays it out.
    var info = chain[t] || {};
    var ea = info.ellipseAspect != null ? info.ellipseAspect : defaultAspect;
    var rx = r;
    var ry = r;
    if (Math.abs(ea - 1) >= 0.01) {
      rx = (2 * r * ea) / (1 + ea);
      ry = (2 * r) / (1 + ea);
    }
    var boost = displayBoost(t);
    var L = MergeAssets.layout(0, 0, rx * boost, t, 0, ry * boost);
    var ratio = info.imageBodyRadiusRatio != null ? info.imageBodyRadiusRatio : MergeAssets.DEFAULT_IMAGE_BODY_RADIUS_RATIO;
    var px = L ? Math.max(L.width, L.height) : (Math.max(rx, ry) * boost) / ratio;
    MergeAssets.noteLayerSize(t, px * res, "jar");
  }
}

/** The Merge jar's flask bounds, sizing and geometry (as getFlaskSystem builds them on Merge). */
function mergeJarModel(game, chain) {
  var layout = typeof game._getLayoutPreview === "function" ? game._getLayoutPreview("shake") : null;
  var bounds = layout && layout.flaskBounds;
  if (!bounds || !(bounds.right > bounds.left) || !(bounds.bottom > bounds.top)) return null;
  var sizing = computeFlaskBodySizing(bounds, chain.length);
  var geometry = null;
  try {
    geometry = buildFlaskGeometry(bounds, sizing.flaskWallThickness).geometry;
  } catch (_e) {
    geometry = null;
  }
  return { layout: layout, sizing: sizing, geometry: geometry };
}

/** The jar glass for the Merge layout, keyed exactly as Game#_drawShakeFlaskDynamicLayer asks. */
function bakeJarGlass(game, model) {
  if (!model || !model.geometry) return null;
  var g = model.geometry;
  var innerW = Math.max(24, g.neckRight - g.neckLeft);
  var innerH = Math.max(24, g.bodyBottom - g.T);
  var corner = typeof game._getShakeJarCornerRadius === "function"
    ? game._getShakeJarCornerRadius(innerW, innerH, model.sizing.bodyRadiusByTier[0])
    : 0;
  var jarLayout = layoutShakeJarChrome(g, { x: 0, y: 0 }, corner, {
    panelHeight: model.layout.flaskPanelHeight,
    resolution: game.pixi ? game.pixi.resolution : game._getCanvasDpr(),
  });
  var art = getShakeJarArt(jarLayout);
  return art ? [art.back, art.front, art.rim] : null;
}

/** Every score a popup can show: catches of each tier, merges into each tier, the dragon bonus. */
function scorePopupTexts(game, chain) {
  var out = [];
  function add(points) {
    var n = Number(points);
    if (!isFinite(n) || n <= 0) return;
    var text = "+" + game.formatScore(n);
    if (out.indexOf(text) < 0) out.push(text);
  }
  for (var t = 0; t < chain.length; t++) {
    if (typeof game.getCatchScore === "function") add(game.getCatchScore(t));
    if (t > 0 && typeof game.getMergeScore === "function") add(game.getMergeScore(t));
  }
  if (typeof game._getDragonMergeScoreBonus === "function") add(game._getDragonMergeScoreBonus());
  return out;
}

/** Popup font peaks for the Fall and the Merge layouts (one value unless the scales differ). */
function scorePopupPeaks(game, fallLayout, mergeLayout) {
  var cfg = C.RUN_CONFIG || {};
  var peaks = [];
  [fallLayout, mergeLayout].forEach(function (layout) {
    if (!layout) return;
    var p = computeScorePopupPeakFontPx(layout.scale, game.mockupScale, cfg);
    if (p > 0 && peaks.indexOf(p) < 0) peaks.push(p);
  });
  return peaks;
}

function skyRadii(game, layout) {
  if (!layout) return null;
  var skyLayout = layout.coreRect && layout.playArea
    ? {
      coreRect: layout.coreRect,
      playArea: layout.playArea,
      leftStrip: layout.leftStrip,
      rightStrip: layout.rightStrip,
      gridUnitPx: layout.gridUnitPx,
      gridWidthPx: layout.gridWidthPx,
      layoutMode: layout.layoutMode,
    }
    : null;
  return skyBodyRadii(skyLayout, { x: 0, y: 0, width: layout.canvasWidth || 0, height: layout.canvasHeight || 0 });
}

/**
 * The warm-up's jobs, most important first. A job may return a canvas (or an array of them)
 * to pre-upload, or a promise to wait for.
 */
function buildJobs(game, state) {
  var jobs = [];
  var pixi = game.pixi || null;
  var res = deviceScale(game);
  var chain = activeChain();
  var fallLayout = null;
  try {
    fallLayout = game.layout || game.getLayout();
  } catch (_e) {
    fallLayout = game.layout || null;
  }
  var merge = mergeJarModel(game, chain);
  var mergeLayout = merge ? merge.layout : null;

  // 1. Juice atlas (the first Fall frame's effects draw from it).
  jobs.push(phase("juice"));
  if (game.juice && typeof game.juice.warm === "function") {
    jobs.push(function () { return game.juice.warm({ yieldTask: state.pace || nextTask }); });
  }

  // 2. Sky pictures.
  jobs.push(phase("sky"));
  var radii = skyRadii(game, fallLayout);
  if (radii) {
    if (pixi && typeof pixi.skyWarmJobs === "function") {
      Array.prototype.push.apply(jobs, pixi.skyWarmJobs(radii));
    } else {
      var dpr2d = typeof game._getCanvasDpr === "function" ? game._getCanvasDpr() : 1;
      [["sun", radii.sun], ["rays", radii.sun], ["sunSet", radii.sun], ["raysSet", radii.sun], ["moon", radii.moon]]
        .forEach(function (k) {
          jobs.push(function () { getCelestialArt(k[0], k[1], dpr2d); });
        });
      jobs.push(function () { getLobeDiscCanvas(); getStarCanvas("sparkle"); getStarCanvas("dot"); getStreakCanvas(); });
    }
  }

  // 3. Creature layers: sizes first (the bake scale is then final), then one layer per job.
  jobs.push(phase("creatures"));
  jobs.push(function () { noteCreatureSizes(game, chain, fallLayout, merge && merge.sizing, res); });
  for (var t = 0; t < chain.length; t++) {
    CREATURE_LAYERS.forEach(function (layer) {
      var tier = t;
      // The glint is one picture shared by every tier: bake it once.
      if (layer === "shine" && tier > 0) return;
      jobs.push(function () { return MergeAssets.layer(tier, layer); });
    });
  }

  // 4. Fall objects (drawn already by the preload; upload only) and the Fall's effect pictures.
  jobs.push(phase("fall-pictures"));
  GAMEPLAY_STATIC_IMAGE_KEYS.forEach(function (key) {
    jobs.push(function () { return getCachedImage(gameImageUrl(key)); });
  });
  Object.keys(FALL_FX_ART).forEach(function (name) {
    jobs.push(function () { return getFallFxArt(name); });
  });

  // 5. Lettering, in the fonts it is drawn in (a fallback-font bake would be redone mid-play).
  jobs.push(phase("lettering"));
  jobs.push(function () { return state.fonts; });
  if (fallLayout && fallLayout.runWidth > 0 && fallLayout.runHeight > 0) {
    var fr = new FallRenderer(fallRunConfig(game), fallLayout.scale, fallLayout.mockupScale);
    var letterDpr = pixi && pixi.resolution > 0 ? pixi.resolution : undefined;
    Array.prototype.push.apply(jobs, fr.letteringWarmJobs(fallLayout.runWidth, fallLayout.runHeight, letterDpr));
    var peaks = scorePopupPeaks(game, fallLayout, mergeLayout);
    scorePopupTexts(game, chain).forEach(function (text) {
      peaks.forEach(function (peak) {
        jobs.push(function () { return bakeScorePopupText(text, peak, letterDpr); });
      });
    });
  }

  // 6. Merge: the glow sprite, the jar marks and the jar glass.
  jobs.push(phase("merge-pictures"));
  if (pixi && typeof pixi.warmFxTextures === "function") {
    jobs.push(function () { pixi.warmFxTextures(); });
  }
  jarMarkArtKeys().forEach(function (key) {
    jobs.push(function () { return getToonArtCanvas(key); });
  });
  jobs.push(function () { return bakeJarGlass(game, merge); });

  // 7. Pop-ups: the DOM pictures a discovery / puzzle reveal shows, encoded in the background;
  // the last job waits for those encodes (capped). The wheel face is already painted at init.
  jobs.push(phase("pop-ups"));
  chain.forEach(function (info) {
    var id = info && info.assetId;
    if (!id || !TIER_ART_SIZES[id]) return;
    jobs.push(function () { state.encodes.push(warmToonArtUrl("tierLarge/" + id)); });
  });
  PUZZLE_PIECE_LAYOUT.forEach(function (piece) {
    if (!piece || !piece.key) return;
    jobs.push(function () { state.encodes.push(warmToonArtUrl(imageArtKey(piece.key))); });
  });
  jobs.push(function () {
    return Promise.race([Promise.all(state.encodes), delay(ENCODE_WAIT_MS)]);
  });
  return jobs;
}

function uploader(game) {
  var pixi = game.pixi;
  if (!pixi || typeof pixi.preupload !== "function") return null;
  var seen = typeof WeakSet === "function" ? new WeakSet() : null;
  return function (out) {
    var list = Array.isArray(out) ? out : [out];
    for (var i = 0; i < list.length; i++) {
      var cv = list[i];
      if (!cv || typeof cv !== "object" || typeof cv.then === "function") continue;
      if (seen) {
        if (seen.has(cv)) continue;
        seen.add(cv);
      }
      pixi.preupload(cv);
    }
  };
}

function runJob(job, upload) {
  var out = null;
  try {
    out = job();
  } catch (err) {
    if (typeof console !== "undefined") console.warn("[warm-up] job failed", err);
    return null;
  }
  if (out && typeof out.then === "function") {
    return out.catch(function (err) {
      if (typeof console !== "undefined") console.warn("[warm-up] job failed", err);
    });
  }
  if (out && upload) upload(out);
  return null;
}

/**
 * What is left past the budget: one job per idle callback, after the loader is gone. Waits while
 * `gate` says no (the first-countdown sky FPS probe), like the art registry's warm queue, so a
 * bake or upload never reads as a slow device there.
 */
function finishInIdle(jobs, upload, gate) {
  var i = 0;
  function step(deadline) {
    if (i >= jobs.length) return;
    if (gate && !gate()) {
      setTimeout(step, 250);
      return;
    }
    if (deadline && !deadline.didTimeout && deadline.timeRemaining() < 4) {
      requestIdleCallback(step, { timeout: 500 });
      return;
    }
    var pending = runJob(jobs[i++], upload);
    var next = function () {
      if (typeof requestIdleCallback === "function") requestIdleCallback(step, { timeout: 500 });
      else setTimeout(step, 30);
    };
    if (pending) pending.then(next, next);
    else next();
  }
  step();
}

/**
 * Draw (and on Pixi upload) every picture play shows, one job per task. Resolves when done, or
 * when the time budget runs out (the rest then continues in idle time). Never rejects.
 * @param {object} game the Game instance (read-only use)
 * @returns {Promise<void>}
 */
export function warmPlayArt(game) {
  if (!game || !canDraw2d()) return Promise.resolve();
  var state = { fonts: loadFonts(), encodes: [] };
  var jobs;
  try {
    jobs = buildJobs(game, state);
  } catch (err) {
    if (typeof console !== "undefined") console.warn("[warm-up] skipped", err);
    return Promise.resolve();
  }
  var upload = uploader(game);
  var pixi = game.pixi;
  var fenceOf = pixi && typeof pixi.gpuFence === "function" ? function () { return pixi.gpuFence(); } : null;
  var fences = [];
  var t0 = now();
  var i = 0;
  // GPU pacing: each step's draws and uploads are fenced, and the next step waits (without
  // blocking the main thread) until at most `inFlight` fenced steps are still on the GPU, so
  // the work never piles up into one long stall of whatever call next needs the GPU process,
  // while the GPU always has the next step queued.
  function settle(inFlight) {
    var limit = inFlight != null ? inFlight : GPU_IN_FLIGHT;
    function drain() {
      // A hidden tab has nothing on screen to stall (and its timers are throttled): no pacing.
      var hidden = typeof document !== "undefined" && document.hidden;
      while (fences.length && (hidden || fences[0].done())) fences.shift().release();
      return fences.length <= limit;
    }
    if (drain()) return null;
    var since = now();
    function check() {
      if (drain()) return null;
      if (now() - since > GPU_WAIT_MS) {
        // Do not hold the loader on a GPU that never reports back: let the oldest go.
        fences.shift().release();
        since = now();
        if (drain()) return null;
      }
      return delay(4).then(check);
    }
    return check();
  }
  function fenceStep() {
    if (!fenceOf) return;
    var f = fenceOf();
    if (f) fences.push(f);
  }
  function afterJob() {
    fenceStep();
    return loop();
  }
  // Steps inside a job (the juice warm-up) pace the same way.
  state.pace = function () {
    fenceStep();
    return nextTask().then(function () { return settle(); });
  };
  function loop() {
    if (i >= jobs.length) {
      // The first Fall frame should not queue behind the last uploads: let the GPU finish.
      return settle(0);
    }
    if (now() - t0 > WARM_BUDGET_MS) {
      while (fences.length) fences.shift().release();
      finishInIdle(jobs.slice(i), upload, function () { return !game._skyAutoProbe; });
      return null;
    }
    return nextTask().then(function () { return settle(); }).then(function () {
      var pending = runJob(jobs[i++], upload);
      if (!pending) return afterJob();
      // A job that never settles (a stalled chunk download) must not hold the loader past the
      // budget: stop waiting for it then; it finishes on its own in the background.
      return Promise.race([pending, delay(Math.max(0, WARM_BUDGET_MS - (now() - t0)))]).then(afterJob);
    });
  }
  mark("play-warmup:start");
  return Promise.resolve().then(loop).catch(function (err) {
    if (typeof console !== "undefined") console.warn("[warm-up] stopped", err);
  }).then(function () {
    mark("play-warmup:end");
    try {
      performance.measure("play-warmup", "play-warmup:start", "play-warmup:end");
    } catch (_e) { /* no user timing */ }
  });
}

/** A job that only marks where a phase starts (user timing `play-warmup:<name>`). */
function phase(name) {
  return function () { mark("play-warmup:" + name); };
}

/** User-timing mark (shows the warm-up in DevTools traces; no-op without the API). */
function mark(name) {
  try {
    if (typeof performance !== "undefined" && typeof performance.mark === "function") performance.mark(name);
  } catch (_e) { /* no user timing */ }
}

/** @internal Test hook: the job list (no running). */
export function __buildWarmJobsForTests(game) {
  return buildJobs(game, { fonts: Promise.resolve(), encodes: [] });
}

/** @internal Test hooks. */
export var __warmInternalsForTests = {
  scorePopupTexts: scorePopupTexts,
  noteCreatureSizes: noteCreatureSizes,
  mergeJarModel: mergeJarModel,
};
